// © Microsoft Corporation. All rights reserved.

/**
 * Enum to set standardized z-index values.
 * @export
 * @enum ZIndex
 * // EXAMPLE:
 * .block {
 *      z-index: ZIndex.Neutral;
 * }
 *
 * BACKGROUND:
 * Providing this ramp ensures that z-index usage is not arbitrary.
 * Adequate room is left in each block for additional levels to be added if needed.
 *
 * USAGE:
 * 1100 (CookieWall): Use for Cookie wall(Compliance).
 * 900's (dialog): Use for Dialog component stack.
 * 700's (flyout): Use for all other flyout elements.
 * 500's (nav): Use for navigational elements.
 * 300's (over): Use for elements that need to be raised more out of the normal flow.
 * 100's (above): Use for elements that need to be raised out of the normal flow.
 * 1 (neutral): Common z-index value for elements that need to be above baseline.
 * 0 (default): Default browser element z-index (baseline).
 * -1 (below): Use for elements that need to live below the normal flow of the document.
 * -2 (buried): Reserved for lowest elements in the stack.
 *
 * OTHER GUIDANCE:
 * Easily increment within a block by adding an integer like the below example.
 * Consider a scenario where you need to increase something just above the navigation level:
 *
 * .block {
 *      z-index: `${ZIndex.Nav + 1}`;
 * }
 *
 * You may also find it useful to increment by other values in the case where you are within a given stacking context.
 *
 * Recommended reading: https://philipwalton.com/articles/what-no-one-told-you-about-z-index/
 *
 * Note: If you have a reason to use a value above CookieWall, Make sure it is not causing a compliance violation.
 */
export enum ZIndex {
    CookieWall = 1100,
    Dialog = 900,
    SmartListFlyout = 800,
    Flyout = 700,
    Overlay = 600,
    Nav = 500,
    Over = 300,
    Above = 100,
    Neutral = 1,
    Default = 0,
    Below = -1,
    Buried = -2
}
